import React, { useEffect, useRef, useState } from 'react';

import type { MediaManagerTemplate, StageRenderTarget } from 'modules/manager/domain/interfaces';
import { useRenderTemplateMutation } from 'modules/manager/store/render-template-api';
import { useLazyStageRenderTargetsQuery } from 'modules/manager/store/stage-render-targets-api';
import { RenderPreviewSkeleton } from 'modules/manager/ui/components/skeletons';
import { deriveAsyncState } from 'shared/utils/async-state';
import UDButton from 'ud-ui/components/button';
import UDAsyncContent from 'ud-ui/components/async-content';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';

import { buildDevRenderParams, splitByStageTargets } from './dev-render-params';

interface RenderPreviewModalProps {
  /** Шаблон, открытый в редакторе. Рендерится его **сохранённая** версия из базы. */
  template: MediaManagerTemplate;
  onClose: () => void;
}

type ErrorStatus = number | 'ABORTED' | undefined;

/** Итог одного запроса рендера: картинка или код ошибки. */
interface RenderResult {
  label?: string;
  imageUrl?: string;
  errorStatus?: ErrorStatus;
}

/**
 * Сообщение по коду ответа, а не по телу: при `responseType: 'blob'` axios отдаёт блобом
 * и тело ошибки, поэтому JSON с описанием оттуда не достать без лишнего чтения.
 */
function errorMessage(status: ErrorStatus): string {
  switch (status) {
    case 403:
      return 'Нет доступа к этому шаблону. Глобальные пресеты не принадлежат организации — сначала сделайте копию.';
    case 404:
      return 'Шаблон не найден. Возможно, он удалён — перезагрузите список шаблонов.';
    case 500:
      return 'Сервер не смог собрать данные или отрисовать картинку. Проверьте, что фикстурные id ещё существуют.';
    default:
      return 'Не удалось получить картинку. Проверьте соединение и попробуйте ещё раз.';
  }
}

function statusOf(error: unknown): ErrorStatus {
  return (error as { status?: ErrorStatus } | undefined)?.status;
}

/** Имя файла: у картинки группы к имени шаблона дописывается группа. */
function fileName(templateName: string, label?: string): string {
  return label ? `${templateName} — ${label}.png` : `${templateName}.png`;
}

/**
 * Превью серверного рендера шаблона: картинки в теле модалки и кнопка «Скачать».
 *
 * Обычно это один `POST /api/render-template`. Исключение — турнирная таблица группового
 * этапа: бэкенд на запрос отдаёт одну картинку, поэтому на каждую группу уходит свой
 * запрос с `groupId`, а список групп модалка сначала читает у этапа.
 *
 * Модалка, а не новая вкладка с `objectURL`: у окна есть куда положить сообщение об
 * отказе (403 на чужом шаблоне — штатный ответ бэкенда), и не теряется контекст «какой
 * именно шаблон рендерили».
 *
 * Монтировать только на время показа — закрытие обязано отпустить и запросы, и объектные
 * адреса картинок.
 */
export function RenderPreviewModal({ template, onClose }: RenderPreviewModalProps) {
  const [renderTemplate] = useRenderTemplateMutation();
  const [loadStageTargets] = useLazyStageRenderTargetsQuery();

  // Результаты приходят все разом: картинки групп показываются вместе, а не по одной,
  // чтобы модалка не прыгала на каждом ответе. `null` — ещё готовимся или ждём ответов.
  const [results, setResults] = useState<RenderResult[] | null>(null);

  // Триггеры держим в ref: RTK Query пересоздаёт их вместе с состоянием, и в зависимостях
  // эффекта они дали бы бесконечный цикл «ответ → новый рендер → ответ».
  const triggersRef = useRef({ renderTemplate, loadStageTargets });
  triggersRef.current = { renderTemplate, loadStageTargets };

  useEffect(() => {
    const controller = new AbortController();
    const requests: { abort: () => void }[] = [];
    const createdUrls: string[] = [];

    setResults(null);

    void (async () => {
      const { renderTemplate: render, loadStageTargets: loadTargets } = triggersRef.current;
      const args = await buildDevRenderParams(template, controller.signal);

      // Модалку успели закрыть, пока качались фоны — запрос уже не нужен.
      if (controller.signal.aborted) return;

      let targets: StageRenderTarget[] = [];

      if (args.stageId !== undefined) {
        const targetsRequest = loadTargets(args.stageId, true);
        requests.push(targetsRequest);

        try {
          targets = await targetsRequest.unwrap();
        } catch (error) {
          if (controller.signal.aborted) return;
          setResults([{ errorStatus: statusOf(error) }]);
          return;
        }

        if (controller.signal.aborted) return;
      }

      const settled = await Promise.allSettled(
        splitByStageTargets(args, targets).map(async ({ label, args: requestArgs }): Promise<RenderResult> => {
          const request = render(requestArgs);
          requests.push(request);

          try {
            const blob = await request.unwrap();
            const imageUrl = URL.createObjectURL(blob);
            createdUrls.push(imageUrl);

            return { label, imageUrl };
          } catch (error) {
            return { label, errorStatus: statusOf(error) };
          }
        }),
      );

      if (controller.signal.aborted) return;

      setResults(settled.map((item) => (item.status === 'fulfilled' ? item.value : {})));
    })();

    return () => {
      controller.abort();
      requests.forEach((request) => request.abort());
      // Объектный адрес живёт ровно столько, сколько показывается картинка.
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [template]);

  const images = (results ?? []).filter((result) => result.imageUrl);
  // Ошибкой всё окно становится, только если не пришло ни одной картинки. Отмена запроса —
  // не ошибка бэкенда: она приходит на размонтировании.
  const failures = (results ?? []).filter((result) => !result.imageUrl && result.errorStatus !== 'ABORTED');
  const isMultiple = (results?.length ?? 0) > 1;

  // Кнопка, а не `<a download>` в футере: вложенная в ссылку китовая кнопка съедала бы
  // клик, а собственная ссылка рядом с UDButton разъезжается с ним по виду.
  const handleDownload = () => {
    images.forEach((image) => {
      const link = document.createElement('a');
      link.href = image.imageUrl as string;
      link.download = fileName(template.name, image.label);
      link.click();
    });
  };

  const state = deriveAsyncState({
    itemsCount: images.length,
    hasCurrentData: images.length > 0,
    isFetching: results === null,
    isError: images.length === 0 && failures.length > 0,
  });

  const imageAlt = (label?: string) =>
    label ? `Рендер шаблона «${template.name}», ${label}` : `Рендер шаблона «${template.name}»`;

  return (
    <Modal show onClose={onClose} size="lg">
      <Modal.Header
        title="Рендер шаблона"
        subtitle={`«${template.name}» — рендерится сохранённая версия шаблона с тестовыми данными`}
      />

      <Modal.Body>
        <UDAsyncContent
          state={state}
          // Высота нужна веткам ошибки; загрузку держит плашка в пропорциях шаблона — картинка
          // того же размера встаёт на её место, и модалка не прыгает.
          minHeight={320}
          loading={<RenderPreviewSkeleton width={template.width} height={template.height} />}
          error={
            <UDText variant="text-2" className="text-inkSecondary px-6 text-center">
              {errorMessage(failures[0]?.errorStatus)}
            </UDText>
          }
        >
          <div className="flex flex-col gap-6">
            {(results ?? []).map((result, index) => (
              <div key={result.label ?? index} className="flex flex-col gap-2">
                {isMultiple && (
                  <UDText variant="text-2" className="text-inkSecondary text-center">
                    {result.label ?? 'Весь этап'}
                  </UDText>
                )}
                {result.imageUrl ? (
                  <img src={result.imageUrl} alt={imageAlt(result.label)} className="rounded-card mx-auto max-h-[60vh]" />
                ) : (
                  result.errorStatus !== 'ABORTED' && (
                    <UDText variant="text-2" className="text-inkSecondary px-6 text-center">
                      {errorMessage(result.errorStatus)}
                    </UDText>
                  )
                )}
              </div>
            ))}
          </div>
        </UDAsyncContent>
      </Modal.Body>

      <Modal.Footer>
        <UDButton variant="secondary" onClick={onClose}>
          Закрыть
        </UDButton>
        {images.length > 0 && (
          <UDButton variant="primary" onClick={handleDownload}>
            {images.length > 1 ? 'Скачать все' : 'Скачать'}
          </UDButton>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default RenderPreviewModal;
